import { useMemo } from 'react'
import DOMPurify from 'dompurify'
import { Image } from 'src/components/ui/Image'

export interface RealStoriesPhotoCardData {
  image?: string | null
  imageAlt?: string | null
  text?: string | null
}

interface RealStoriesPhotoCardProps {
  card: RealStoriesPhotoCardData
  rotationClass: string
}

const BLOCKED_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'style']

interface SanitizedRealStoriesPhotoCardData {
  image: string
  imageAlt: string
  text: string
}

function sanitizeRealStoriesCard(
  card: RealStoriesPhotoCardData | null | undefined
): SanitizedRealStoriesPhotoCardData | null {
  if (!card) {
    return null
  }

  const image = card.image?.trim() ?? ''
  const imageAlt = card.imageAlt?.trim() ?? ''
  const sanitizedText =
    typeof DOMPurify?.sanitize === 'function'
      ? DOMPurify.sanitize(card.text ?? '', {
          FORBID_TAGS: BLOCKED_TAGS,
        })
      : ''

  if (!image || !imageAlt || !sanitizedText.trim()) {
    return null
  }

  return {
    image,
    imageAlt,
    text: sanitizedText,
  }
}

export function isRenderableRealStoriesCard(
  card: RealStoriesPhotoCardData | null | undefined
): boolean {
  return sanitizeRealStoriesCard(card) !== null
}

function RealStoriesPhotoCard({
  card,
  rotationClass,
}: Readonly<RealStoriesPhotoCardProps>) {
  const sanitizedCard = useMemo(() => sanitizeRealStoriesCard(card), [card])

  if (!sanitizedCard) {
    return null
  }

  return (
    <li
      className={`w-full max-w-[130px] pp:max-w-[150px] p:max-w-[163px] sm:max-w-[274px] ${rotationClass}`}
    >
      <article className="relative border border-white bg-white p-3 shadow-[0_14px_24px_rgba(0,0,0,0.35)]">
        <span
          aria-hidden
          className="absolute left-1/2 top-0 h-5 w-16 -translate-x-1/2 -translate-y-1/2 bg-vivid-yellow/[92%]"
        />

        <Image
          src={sanitizedCard.image}
          alt={sanitizedCard.imageAlt}
          className="md:h-[250px] w-full object-cover"
        />

        <div
          className="break-words text-xs sm:text-sm font-semibold leading-5 text-salesForceTitle"
          dangerouslySetInnerHTML={{ __html: sanitizedCard.text }}
        />
      </article>
    </li>
  )
}

export default RealStoriesPhotoCard
